import { OnApproveData } from '@paypal/paypal-js/types/components/buttons';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { ReactPayPalScriptOptions } from '@paypal/react-paypal-js/dist/types/types/scriptProviderTypes';
import { Dispatch, SetStateAction } from 'react';

import PayPalButton from '../PayPalButton/PayPalButton';

import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import PayPalButtonContextProvider from '@providers/PayPalButtonContextProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import { useRetryPaymentContext } from '@providers/RetryPaymentContextProvider';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { initiatePaypalPayment } from '@services/paymentService';
import { captureException } from '@services/sentryService';

type PayPalRetryButtonProps = {
  currency: string;
  orderId: string;
  siteId: number;
  amount: number;
  setLoading: Dispatch<SetStateAction<boolean>>;
};

const PayPalRetryButton = ({
  currency,
  orderId,
  siteId,
  amount,
  setLoading,
}: PayPalRetryButtonProps) => {
  const { csrfToken } = useAppContext();
  const {
    config: {
      paypal: { clientId },
    },
  } = useConfig();
  const {
    retryState: { retryData },
  } = useRetryState();
  const { setRetryPaymentInfo } = useRetryPaymentContext();

  const payPalJsSDKOptions: ReactPayPalScriptOptions = {
    clientId,
    currency,
    intent: 'authorize',
  };

  const style: any = {
    tagline: false,
    layout: 'horizontal',
    height: 36,
  };

  const onButtonClicked = () => {
    AdobeTrackingService.accountEventTracking('retry_payment', {
      payment_method: 'PAYPAL',
      order_id: orderId,
    });
  };

  const createOrder = async (): Promise<string> => {
    // Do a casting because we know in PayPal case, "paypalOrderId" is defined.
    return retryData?.paypalOrderId as string;
  };

  const onApprove = async (data: OnApproveData): Promise<void> => {
    setLoading(true);
    try {
      const retryPaymentStatus = await initiatePaypalPayment({
        csrfToken: csrfToken as string,
        tokenId: data.orderID,
        paymentAmount: amount,
        orderId,
        siteId,
        currency,
      });
      const retryPossible =
        retryPaymentStatus === 'CANCELLED' || retryPaymentStatus === 'UNSUCCESSFUL';
      setRetryPaymentInfo({ retryPossible, retryPaymentStatus });
    } catch (e: any) {
      captureException({
        message: 'Error while retrying PayPal payment',
        error: e,
      });
      setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'UNKNOWN' });
    } finally {
      setLoading(false);
    }
  };

  const onError = (err: any): void => {
    captureException({ message: 'Error received from Paypal button', error: err });
    setLoading(false);
    setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'CANCELLED' });
  };

  return (
    <PayPalScriptProvider options={payPalJsSDKOptions}>
      <PayPalButtonContextProvider onApprove={onApprove}>
        <PayPalButton
          style={style}
          onClick={onButtonClicked}
          createOrder={createOrder}
          onError={onError}
        />
      </PayPalButtonContextProvider>
    </PayPalScriptProvider>
  );
};

export default PayPalRetryButton;
